/**
 * Smart Cashier Console
 *
 * คอนโซลฝากถอนอัจฉริยะ - Hybrid Layout
 * - 15% Bank Balance Bar (Fixed Header)
 * - 40% Pending Transactions (Sticky Action Zone)
 * - 45% History (Scrollable)
 * - Collapsible Bank Feed Sidebar
 *
 * ใช้ components เดิมจาก pending และ history page
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  RefreshCw,
  Clock,
  History,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
  Check,
  PanelRightClose,
  ArrowRightLeft,
  Plus,
  Wallet,
  XCircle,
  SlidersHorizontal,
  Eye,
  Search,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
  Copy,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth";
import { useCurrency } from "@/stores/currency-store";
import { useTranslation } from "react-i18next";
import { TransactionService } from "@/domain/transaction";
import { BankService } from "@/domain/bank";
import { getUserService } from "@/domain/user";
import { findBank } from "@/data/banks";
import { toast } from "sonner";
import { SlipLogService, type SlipLog } from "@/domain/slip-log";
import { LineConnectLogService } from "@/domain/line-connect-log/line-connect-log.service";
import type { LineConnectLog } from "@/domain/line-connect-log/line-connect-log.types";

// 🔗 ใช้ Components เดิมที่ทำงานได้แล้ว
import {
  TransactionFiltersSection,
  TransactionTable,
  TransactionDialog,
  ReasonDialog,
  ApproveBankDialog,
  type Transaction,
  type BankAccount,
  type TransactionFilters,
  type TransactionDialogMode,
  type TransactionFormData,
} from "./components";
import { MemberDetailDialog } from "@/features/members/member-detail-dialog";
import type { Member } from "@/domain/user/user.types";

// ============================================
// Types
// ============================================

interface BankFeedItem {
  id: string;
  source: "SlipLog" | "LineConnectLog"; // ✅ Bank Feed ดึงเฉพาะจาก SlipLog และ LineConnectLog
  transRef: string;
  amount: number;
  senderName: string;
  senderBank: string;
  senderAccount: string;
  receiverBank: string;
  receiverAccount: string;
  timestamp: string;
  isMatched: boolean;
  matchedMember?: string;
  status?: string; // ✅ เพิ่ม status
  rawData?: SlipLog | LineConnectLog; // ✅ Bank Feed ดึงเฉพาะจาก SlipLog และ LineConnectLog
  isDuplicate?: boolean; // ✅ เพิ่ม flag สำหรับ duplicate
  duplicateReason?: string; // ✅ เหตุผลที่ถูกปฏิเสธ
}

// ============================================
// Mock Bank Feed Data (ไม่ใช้แล้ว - ใช้ข้อมูลจริงจาก SlipLog และ LineConnectLog)
// ============================================

// ============================================
// Bank Log Types & Mock Data
// ============================================

interface BankLogItem {
  id: string;
  type: "in" | "out";
  amount: number;
  balance: number;
  description: string;
  senderName?: string;
  senderBank?: string;
  senderAccount?: string;
  receiverName?: string;
  receiverBank?: string;
  receiverAccount?: string;
  transRef: string;
  timestamp: string;
  status: "success" | "pending" | "failed";
  matchedMember?: string;
  matchedTxId?: string;
}

// Generate mock bank log data
// ============================================
// Bank Log Dialog Component
// ============================================

interface BankLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bank: BankAccount | null;
}

/**
 * ✅ Helper: Extract name from LINE notification title/msg
 * Example: "โอนเงิน จากคุณ นาย A" → "นาย A"
 */
function extractName(text: string): string {
  // Try to extract name after "จากคุณ" or "จาก"
  const fromMatch = text.match(/จากคุณ\s*(.+?)(?:\s|$|\.)/);
  if (fromMatch) return fromMatch[1].trim();

  const fromMatch2 = text.match(/จาก\s*(.+?)(?:\s|$|\.)/);
  if (fromMatch2) return fromMatch2[1].trim();

  // Try to extract name after "ไปยัง" or "ถึง"
  const toMatch = text.match(/(?:ไปยัง|ถึง)\s*(.+?)(?:\s|$|\.)/);
  if (toMatch) return toMatch[1].trim();

  return "ไม่ระบุ";
}

function BankLogDialog({ open, onOpenChange, bank }: BankLogDialogProps) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const { sessionToken } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [filterType, setFilterType] = useState<"all" | "in" | "out">("all");
  const [logs, setLogs] = useState<BankLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Load real LineConnectLog data
  const loadLineConnectLogs = useCallback(async () => {
    if (!bank || !sessionToken) return;

    setIsLoading(true);
    try {
      // Query LineConnectLog filtered by bank account number
      // Match myNumber (last 4 digits) with bankAccount (last 4 digits)
      const last4Digits = bank.bankAccount.replace(/\D/g, "").slice(-4);

      console.log("🏦 [BankLogDialog] Loading logs for bank:", {
        bankId: bank.id,
        bankAccount: bank.bankAccount,
        last4Digits,
      });

      // Get all LineConnectLog records (we'll filter client-side by account number)
      const result = await LineConnectLogService.getAll(sessionToken, {
        limit: 500, // ✅ เพิ่มเป็น 500 rows สำหรับตรวจสอบข้อมูล
      });

      // Filter by matching last 4 digits of account number
      const matchedLogs = result.results.filter((log) => {
        if (!log.myNumber) return false;
        const logLast4 = log.myNumber.replace(/\D/g, "").slice(-4);
        return logLast4 === last4Digits;
      });

      console.log("🏦 [BankLogDialog] Matched logs:", matchedLogs.length);

      // Map LineConnectLog to BankLogItem format
      const mappedLogs: BankLogItem[] = matchedLogs.map((log) => ({
        id: log.id,
        type: log.type === "Deposit" ? "in" : "out",
        amount: log.amount || 0,
        balance: 0, // LineConnectLog doesn't have running balance
        description: log.title || log.msg || "",
        senderName: log.type === "Deposit" ? extractName(log.title || log.msg || "") : undefined,
        senderBank: log.type === "Deposit" ? log.bank : undefined,
        receiverName: log.type === "Withdraw" ? extractName(log.title || log.msg || "") : undefined,
        receiverBank: log.type === "Withdraw" ? log.bank : undefined,
        transRef: log.objectId.slice(0, 8).toUpperCase(),
        timestamp: log.dateTimeFormat || log.createdAt || new Date().toISOString(),
        status: log.status === "Approved" ? "success" : log.status === "Pending" ? "pending" : "failed",
        matchedMember: undefined, // Not available in LineConnectLog
      }));

      setLogs(mappedLogs);
    } catch (error) {
      console.error("❌ [BankLogDialog] Error loading logs:", error);
      toast.error("ไม่สามารถโหลดรายการธุรกรรมได้");
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [bank, sessionToken]);

  // Load data when dialog opens
  useEffect(() => {
    if (open && bank) {
      loadLineConnectLogs();
      setSearchText("");
      setFilterType("all");
    }
  }, [open, bank, loadLineConnectLogs]);

  // Reset and load when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  if (!bank) return null;

  const filteredLogs = logs.filter((log) => {
    if (filterType !== "all" && log.type !== filterType) return false;
    if (searchText) {
      const search = searchText.toLowerCase();
      return (
        log.transRef.toLowerCase().includes(search) ||
        log.senderName?.toLowerCase().includes(search) ||
        log.receiverName?.toLowerCase().includes(search) ||
        log.matchedMember?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const totalIn = logs
    .filter((l) => l.type === "in")
    .reduce((sum, l) => sum + l.amount, 0);
  const totalOut = logs
    .filter((l) => l.type === "out")
    .reduce((sum, l) => sum + l.amount, 0);
  const countIn = logs.filter((l) => l.type === "in").length;
  const countOut = logs.filter((l) => l.type === "out").length;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`คัดลอก: ${text}`);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-violet-500/20 flex items-center justify-center">
              {bank.bankImage ? (
                <img
                  src={bank.bankImage}
                  alt={bank.bankName}
                  className="w-10 h-10 object-contain rounded-lg"
                />
              ) : (
                <Building2 className="w-6 h-6 text-primary" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span>{bank.bankName}</span>
                <Badge variant="outline" className="text-xs">
                  {bank.name}
                </Badge>
                {bank.apiStatus === "active" && (
                  <Badge className="bg-emerald-500 text-white text-[10px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1" />
                    API Active
                  </Badge>
                )}
              </div>
              <p className="text-sm font-mono text-muted-foreground">
                {bank.bankAccount}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 py-3 border-b">
          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-muted-foreground">{t('transactions.moneyIn')}</span>
            </div>
            <p className="text-lg font-bold text-emerald-600">
              +{formatCurrency(totalIn)}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {countIn} {t('transactions.items')}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border border-orange-200/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-orange-600" />
              <span className="text-xs text-muted-foreground">{t('transactions.moneyOut')}</span>
            </div>
            <p className="text-lg font-bold text-orange-600">
              -{formatCurrency(totalOut)}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {countOut} {t('transactions.items')}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200/50">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-muted-foreground">ยอดปัจจุบัน</span>
            </div>
            <p className="text-lg font-bold text-blue-600">
              {formatCurrency(bank.amount)}
            </p>
            <p className="text-[10px] text-muted-foreground">อัปเดตล่าสุด</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-3 py-3 border-b">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('transactions.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <div className="flex gap-1">
            <Button
              variant={filterType === "all" ? "secondary" : "ghost"}
              size="sm"
              className="h-9"
              onClick={() => setFilterType("all")}
            >
              {t('transactions.all')}
            </Button>
            <Button
              variant={filterType === "in" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 text-emerald-600"
              onClick={() => setFilterType("in")}
            >
              <ArrowDownLeft className="w-3.5 h-3.5 mr-1" />
              เข้า
            </Button>
            <Button
              variant={filterType === "out" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 text-orange-600"
              onClick={() => setFilterType("out")}
            >
              <ArrowUpRight className="w-3.5 h-3.5 mr-1" />
              ออก
            </Button>
          </div>
          <Button variant="outline" size="sm" className="h-9">
            <Download className="w-3.5 h-3.5 mr-1" />
            Export
          </Button>
        </div>

        {/* Transaction List */}
        <div className="flex-1 overflow-y-auto min-h-[300px] space-y-2 py-3">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Loader2 className="w-12 h-12 mb-2 animate-spin" />
              <p>กำลังโหลดรายการ...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Search className="w-12 h-12 mb-2 opacity-20" />
              <p>ไม่พบรายการ</p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div
                key={log.id}
                className={cn(
                  "p-3 rounded-lg border transition-colors hover:shadow-sm",
                  log.type === "in"
                    ? "bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200/50 hover:bg-emerald-50"
                    : "bg-orange-50/50 dark:bg-orange-950/10 border-orange-200/50 hover:bg-orange-50"
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Icon + Info */}
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                        log.type === "in"
                          ? "bg-emerald-500/20"
                          : "bg-orange-500/20"
                      )}
                    >
                      {log.type === "in" ? (
                        <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-orange-600" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={cn(
                            "font-bold text-lg",
                            log.type === "in"
                              ? "text-emerald-600"
                              : "text-orange-600"
                          )}
                        >
                          {log.type === "in" ? "+" : "-"}
                          {formatCurrency(log.amount)}
                        </span>
                      </div>

                      {/* ✅ Bank Message - ข้อความจากธนาคาร (แสดงเต็ม) */}
                      {log.description && (
                        <div className="mb-2 p-2 rounded bg-muted/30 border border-border/50">
                          <p className="text-sm text-foreground leading-relaxed break-words whitespace-normal">
                            {log.description}
                          </p>
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {log.type === "in" && log.senderName && (
                          <p>
                            {t('transactions.from')}{" "}
                            <span className="font-medium text-foreground">
                              {log.senderName}
                            </span>{" "}
                            ({log.senderBank})
                          </p>
                        )}
                        {log.type === "out" && log.receiverName && (
                          <p>
                            ไปยัง:{" "}
                            <span className="font-medium text-foreground">
                              {log.receiverName}
                            </span>{" "}
                            ({log.receiverBank})
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px]">
                            {log.transRef}
                          </span>
                          <button
                            onClick={() => copyToClipboard(log.transRef)}
                            className="p-0.5 hover:bg-muted rounded"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {log.matchedMember && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <Badge className="bg-primary/10 text-primary text-[10px] h-5">
                            <Check className="w-3 h-3 mr-1" />
                            จับคู่: {log.matchedMember}
                          </Badge>
                          {log.matchedTxId && (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              {log.matchedTxId}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Time + Balance */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono font-medium">
                      {new Date(log.timestamp).toLocaleTimeString("th-TH", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(log.timestamp).toLocaleDateString("th-TH", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      คงเหลือ:{" "}
                      <span className="font-mono">
                        {formatCurrency(log.balance)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Bank Accounts Dialog Component (แยก Deposit/Withdraw)
// ============================================

interface BankAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  banks: BankAccount[];
  type: "deposit" | "withdraw";
  onViewLog: (bank: BankAccount) => void;
}

function BankAccountsDialog({
  open,
  onOpenChange,
  banks,
  type,
  onViewLog,
}: BankAccountsDialogProps) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();

  const isDeposit = type === "deposit";
  const filteredBanks = banks.filter((b) =>
    isDeposit
      ? b.type === "Deposit" || b.type === "Both"
      : b.type === "Withdraw" || b.type === "Both"
  );
  const total = filteredBanks.reduce((sum, b) => sum + b.amount, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div
              className={cn(
                "p-2 rounded-lg",
                isDeposit ? "bg-emerald-500/20" : "bg-orange-500/20"
              )}
            >
              {isDeposit ? (
                <ArrowDownToLine className="w-5 h-5 text-emerald-600" />
              ) : (
                <ArrowUpFromLine className="w-5 h-5 text-orange-600" />
              )}
            </div>
            <div>
              <span
                className={isDeposit ? "text-emerald-700" : "text-orange-700"}
              >
                {isDeposit ? "บัญชีรับฝาก" : "บัญชีถอน"}
              </span>
              <Badge variant="outline" className="ml-2">
                {filteredBanks.length} บัญชี
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Summary */}
        <div
          className={cn(
            "p-4 rounded-lg border",
            isDeposit
              ? "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200/50"
              : "bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border-orange-200/50"
          )}
        >
          <p className="text-sm text-muted-foreground mb-1">ยอดรวม</p>
          <p
            className={cn(
              "text-2xl font-bold",
              isDeposit ? "text-emerald-600" : "text-orange-600"
            )}
          >
            {formatCurrency(total)}
          </p>
        </div>

        {/* Bank List */}
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {filteredBanks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Building2 className="w-12 h-12 mb-2 opacity-20" />
              <p>{isDeposit ? t('transactions.noAccountsDeposit') : t('transactions.noAccountsWithdraw')}</p>
            </div>
          ) : (
            filteredBanks.map((bank) => {
              const isWarning = isDeposit
                ? bank.amount > 500000
                : bank.amount < 10000;
              return (
                <div
                  key={bank.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border hover:shadow-md transition-all cursor-pointer",
                    isDeposit
                      ? "bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50 hover:bg-emerald-50"
                      : "bg-orange-50/50 dark:bg-orange-900/10 border-orange-200/50 hover:bg-orange-50"
                  )}
                  onClick={() => onViewLog(bank)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center border overflow-hidden shadow-sm">
                      {bank.bankImage ? (
                        <img
                          src={bank.bankImage}
                          alt={bank.bankName}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <Building2
                          className={cn(
                            "w-6 h-6",
                            isDeposit ? "text-emerald-600" : "text-orange-600"
                          )}
                        />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{bank.bankName}</p>
                        <Badge variant="secondary" className="text-[10px] h-5">
                          {bank.name}
                        </Badge>
                        {bank.apiStatus === "active" && (
                          <span className="flex items-center text-[10px] text-emerald-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse mr-1" />
                            API
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">
                        {bank.bankAccount}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={cn(
                        "text-lg font-bold",
                        isWarning
                          ? isDeposit
                            ? "text-amber-600"
                            : "text-red-600"
                          : isDeposit
                          ? "text-emerald-600"
                          : "text-orange-600"
                      )}
                    >
                      {formatCurrency(bank.amount)}
                    </p>
                    <div className="flex items-center justify-end gap-1 text-xs text-primary">
                      <Eye className="w-3 h-3" />
                      <span>{t('transactions.viewList')}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Balance Bar Component (Compact with Dialog)
// ============================================

function BalanceBar({ banks }: { banks: BankAccount[] }) {
  const { format: formatCurrency } = useCurrency();
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankDialogType, setBankDialogType] = useState<"deposit" | "withdraw">(
    "deposit"
  );
  const [selectedBank, setSelectedBank] = useState<BankAccount | null>(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);

  const openBankDialog = (type: "deposit" | "withdraw") => {
    setBankDialogType(type);
    setBankDialogOpen(true);
  };

  const depositBanks = banks.filter(
    (b) => b.type === "Deposit" || b.type === "Both"
  );
  const withdrawBanks = banks.filter(
    (b) => b.type === "Withdraw" || b.type === "Both"
  );

  const totalDeposit = depositBanks.reduce((sum, b) => sum + b.amount, 0);
  const totalWithdraw = withdrawBanks.reduce((sum, b) => sum + b.amount, 0);

  // Check for alerts
  const lowBalanceWithdraw = withdrawBanks.some((b) => b.amount < 10000);
  const overLimitDeposit = depositBanks.some((b) => b.amount > 500000);

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Deposit Vaults - Clickable */}
      <Card
        className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200/50 dark:border-emerald-800/50 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all"
        onClick={() => openBankDialog("deposit")}
      >
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-500/20">
                <ArrowDownToLine className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">บัญชีรับฝาก</p>
                <p className="text-lg font-bold text-emerald-600">
                  {formatCurrency(totalDeposit)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {overLimitDeposit && (
                <Badge
                  variant="outline"
                  className="bg-amber-500/20 text-amber-600 border-amber-500/30"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Over Limit
                </Badge>
              )}
              <Eye className="w-4 h-4 text-emerald-400" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {depositBanks.slice(0, 3).map((bank) => (
              <div
                key={bank.id}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs",
                  bank.amount > 500000
                    ? "bg-amber-100 dark:bg-amber-900/30 border border-amber-300"
                    : "bg-white/60 dark:bg-white/5 border border-emerald-200/50"
                )}
              >
                {bank.bankImage ? (
                  <img
                    src={bank.bankImage}
                    alt={bank.bankName}
                    className="w-5 h-5 rounded object-contain"
                  />
                ) : (
                  <Building2 className="w-4 h-4 text-emerald-600" />
                )}
                <span className="font-medium">{bank.name}</span>
                <span
                  className={cn(
                    "font-mono",
                    bank.amount > 500000 ? "text-amber-600" : "text-emerald-600"
                  )}
                >
                  {formatCurrency(bank.amount)}
                </span>
                {bank.apiStatus === "active" && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                )}
              </div>
            ))}
            {depositBanks.length > 3 && (
              <span className="text-xs text-emerald-600">
                +{depositBanks.length - 3}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payout Wallets - Clickable */}
      <Card
        className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border-orange-200/50 dark:border-orange-800/50 cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
        onClick={() => openBankDialog("withdraw")}
      >
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-500/20">
                <ArrowUpFromLine className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">บัญชีถอน</p>
                <p className="text-lg font-bold text-orange-600">
                  {formatCurrency(totalWithdraw)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {lowBalanceWithdraw && (
                <Badge
                  variant="outline"
                  className="bg-red-500/20 text-red-600 border-red-500/30"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Low Balance
                </Badge>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-orange-300 text-orange-600 hover:bg-orange-100"
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Open top-up dialog
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                เติมเงิน
              </Button>
              <Eye className="w-4 h-4 text-orange-400" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {withdrawBanks.slice(0, 3).map((bank) => (
              <div
                key={bank.id}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs",
                  bank.amount < 10000
                    ? "bg-red-100 dark:bg-red-900/30 border border-red-300"
                    : "bg-white/60 dark:bg-white/5 border border-orange-200/50"
                )}
              >
                {bank.bankImage ? (
                  <img
                    src={bank.bankImage}
                    alt={bank.bankName}
                    className="w-5 h-5 rounded object-contain"
                  />
                ) : (
                  <Building2 className="w-4 h-4 text-orange-600" />
                )}
                <span className="font-medium">{bank.name}</span>
                <span
                  className={cn(
                    "font-mono",
                    bank.amount < 10000 ? "text-red-600" : "text-orange-600"
                  )}
                >
                  {formatCurrency(bank.amount)}
                </span>
                {bank.apiStatus === "active" && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                )}
              </div>
            ))}
            {withdrawBanks.length > 3 && (
              <span className="text-xs text-orange-600">
                +{withdrawBanks.length - 3}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bank Accounts Dialog */}
      <BankAccountsDialog
        open={bankDialogOpen}
        onOpenChange={setBankDialogOpen}
        banks={banks}
        type={bankDialogType}
        onViewLog={(bank) => {
          setSelectedBank(bank);
          setLogDialogOpen(true);
        }}
      />

      {/* Bank Log Dialog */}
      <BankLogDialog
        open={logDialogOpen}
        onOpenChange={setLogDialogOpen}
        bank={selectedBank}
      />
    </div>
  );
}

// ============================================
// Bank Feed Sidebar Component
// ============================================

function BankFeedSidebar({
  isOpen,
  onToggle,
  bankFeed,
  isLoading,
  selectedDateRange,
  onDateChange,
}: {
  isOpen: boolean;
  onToggle: () => void;
  bankFeed: BankFeedItem[];
  isLoading: boolean;
  selectedDateRange?: { from: Date; to?: Date };
  onDateChange?: (range: { from: Date; to?: Date } | undefined) => void;
}) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const [filter, setFilter] = useState<"all" | "matched" | "unmatched">("all");

  // ✅ Filter by date range if selected
  let dateFilteredFeed = bankFeed;
  if (selectedDateRange) {
    const { from, to } = selectedDateRange;
    const fromDate = new Date(from);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = to ? new Date(to) : new Date(from);
    toDate.setHours(23, 59, 59, 999);

    dateFilteredFeed = bankFeed.filter((item) => {
      const itemDate = new Date(item.timestamp);
      return itemDate >= fromDate && itemDate <= toDate;
    });
  }

  const filteredFeed = dateFilteredFeed.filter((item) => {
    if (filter === "matched") return item.isMatched;
    if (filter === "unmatched") return !item.isMatched && !item.isDuplicate; // ✅ ไม่นับ duplicate
    return true;
  });

  // ✅ Count unmatched (ไม่นับ duplicate)
  const matchedCount = dateFilteredFeed.filter((i) => i.isMatched).length;
  const unmatchedCount = dateFilteredFeed.filter(
    (i) => !i.isMatched && !i.isDuplicate
  ).length;
  const duplicateCount = dateFilteredFeed.filter((i) => i.isDuplicate).length;

  // ปุ่มเปิด Bank Feed (แสดงเมื่อ sidebar ปิด)
  if (!isOpen) {
    return null; // ปุ่มจะแสดงใน header แทน
  }

  return (
    <div className="w-72 border-l bg-card flex flex-col h-full shadow-lg">
      <div className="p-3 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-500/20">
            <ArrowRightLeft className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <span className="font-medium text-sm">Live Bank Feed</span>
            <div className="flex gap-2 text-[10px]">
              <span className="text-emerald-600">✓ {matchedCount}</span>
              <span className="text-amber-600">○ {unmatchedCount}</span>
              {duplicateCount > 0 && (
                <span className="text-red-600">⚠ {duplicateCount}</span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onToggle}
        >
          <PanelRightClose className="w-4 h-4" />
        </Button>
      </div>

      {/* Date Range Picker */}
      {onDateChange && (
        <div className="p-2 border-b">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDateRange ? (
                  selectedDateRange.to ? (
                    <span>
                      {selectedDateRange.from.toLocaleDateString("th-TH", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}{" "}
                      -{" "}
                      {selectedDateRange.to.toLocaleDateString("th-TH", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  ) : (
                    selectedDateRange.from.toLocaleDateString("th-TH", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  )
                ) : (
                  <span>{t('transactions.selectDateRange')}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={selectedDateRange}
                onSelect={(range: { from?: Date; to?: Date } | undefined) => {
                  if (range && range.from) {
                    onDateChange({
                      from: range.from,
                      to: range.to,
                    });
                  } else {
                    onDateChange(undefined);
                  }
                }}
                numberOfMonths={2}
                initialFocus
              />
              <div className="p-2 border-t flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    // Set default: 7 days ago to today (local time)
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(today.getDate() - 7);
                    sevenDaysAgo.setHours(0, 0, 0, 0);
                    onDateChange({ from: sevenDaysAgo, to: today });
                  }}
                >
                  7 วันย้อนหลัง
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    // ✅ ล้างเวลา = แสดงทั้งหมด
                    onDateChange(undefined);
                  }}
                >
                  {t('transactions.showAll')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex border-b px-2 py-1.5 gap-1">
        <Button
          variant={filter === "all" ? "secondary" : "ghost"}
          size="sm"
          className="h-6 text-xs flex-1"
          onClick={() => setFilter("all")}
        >
          {t('transactions.allWithCount', { count: dateFilteredFeed.length })}
        </Button>
        <Button
          variant={filter === "matched" ? "secondary" : "ghost"}
          size="sm"
          className="h-6 text-xs flex-1"
          onClick={() => setFilter("matched")}
        >
          Matched ({matchedCount})
        </Button>
        <Button
          variant={filter === "unmatched" ? "secondary" : "ghost"}
          size="sm"
          className="h-6 text-xs flex-1"
          onClick={() => setFilter("unmatched")}
        >
          Unmatched ({unmatchedCount})
        </Button>
      </div>

      {/* Feed List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredFeed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ArrowRightLeft className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">{t('transactions.noData')}</p>
            </div>
          ) : (
            filteredFeed.map((item) => (
              <Card
                key={item.id}
                className={cn(
                  "p-2.5",
                  item.isMatched
                    ? "border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20"
                    : "border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20"
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-lg font-bold font-mono text-emerald-600">
                        +{formatCurrency(item.amount)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[9px] px-1 py-0">
                        {item.source === "SlipLog" ? "Slip" : "LINE"}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {item.transRef}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    {/* ✅ แสดงเวลา - ใหญ่ชัดเจน */}
                    <div className="text-sm font-bold font-mono text-foreground">
                      {new Date(item.timestamp).toLocaleTimeString("th-TH", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    {/* ✅ แสดงวันที่ - เล็กกว่า */}
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(item.timestamp).toLocaleDateString("th-TH", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-10">From:</span>
                    <span className="font-medium">{item.senderName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="w-10"></span>
                    <span>
                      {item.senderBank} • {item.senderAccount}
                    </span>
                  </div>
                  {item.receiverBank && item.receiverAccount && (
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="text-muted-foreground w-10">To:</span>
                      <span>
                        {item.receiverBank} • {item.receiverAccount}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-2 pt-2 border-t border-border/50">
                  {item.isMatched ? (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30 text-[10px]"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Matched
                      </Badge>
                      <span className="text-xs text-primary font-medium">
                        {item.matchedMember}
                      </span>
                    </div>
                  ) : item.isDuplicate ? (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-red-500/20 text-red-600 border-red-500/30 text-[10px]"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Duplicate
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {item.duplicateReason}
                      </span>
                    </div>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-[10px] w-full justify-center"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Unmatched
                    </Badge>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function SmartCashierPage() {
  const { sessionToken } = useAuth();
  const { format: formatCurrency } = useCurrency();
  const { t } = useTranslation();

  // ============================================
  // Pending Section State (เหมือน transactions-pending-page)
  // ============================================
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>(
    []
  );
  const [pendingBanks, setPendingBanks] = useState<BankAccount[]>([]);
  const [isPendingLoading, setIsPendingLoading] = useState(true);
  const [pendingFilters, setPendingFilters] = useState<TransactionFilters>({
    searchText: "",
    type: "",
    channel: "",
    bankType: "",
  });

  // Bulk Action State
  const [rejectAllDialog, setRejectAllDialog] = useState(false);
  const [isRejectingAll, setIsRejectingAll] = useState(false);
  const [rejectProgress, setRejectProgress] = useState({
    current: 0,
    total: 0,
  });

  // ============================================
  // History Section State (เหมือน transactions-history-page)
  // ============================================
  const [historyTransactions, setHistoryTransactions] = useState<Transaction[]>(
    []
  );
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(20);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyFilters, setHistoryFilters] = useState<TransactionFilters>({
    searchText: "",
    type: "",
    channel: "",
    bankType: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // ============================================
  // Dialog States (เหมือนเดิม)
  // ============================================
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<TransactionDialogMode>("view");
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  // Member Detail Dialog
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isLoadingMember, setIsLoadingMember] = useState(false);

  // ✅ V2: Reason Dialog (สำหรับ Cancel/Reject พร้อมเหตุผล)
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonDialogType, setReasonDialogType] = useState<"reject" | "cancel">(
    "reject"
  );
  const [reasonDialogTransaction, setReasonDialogTransaction] =
    useState<Transaction | null>(null);

  // ✅ ApproveBankDialog State (สำหรับเลือกบัญชีถอน)
  const [approveBankDialogOpen, setApproveBankDialogOpen] = useState(false);
  const [approveBankDialogType, setApproveBankDialogType] = useState<
    "manual" | "auto"
  >("manual");
  const [approveBankDialogTransaction, setApproveBankDialogTransaction] =
    useState<Transaction | null>(null);


  // ============================================
  // Sidebar State
  // ============================================
  const [sidebarOpen, setSidebarOpen] = useState(false); // ซ่อนไว้ก่อน
  const [bankFeed, setBankFeed] = useState<BankFeedItem[]>([]);
  const [isLoadingBankFeed, setIsLoadingBankFeed] = useState(false);
  // ✅ Default: 7 วันล่าสุด (วันนี้ - 7 วัน) - ใช้ useCallback เพื่อไม่ให้เปลี่ยนทุกครั้ง
  const getDefaultDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // สิ้นสุดวันนี้
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0); // เริ่มต้นวันนั้น
    return { from: sevenDaysAgo, to: today };
  }, []); // ✅ ไม่มี dependency เพื่อให้ function เดิมเสมอ

  const [bankFeedDateRange, setBankFeedDateRange] = useState<
    | {
        from: Date;
        to?: Date;
      }
    | undefined
  >(() => getDefaultDateRange()); // ✅ เริ่มต้นเป็น 7 วันล่าสุด

  // ============================================
  // V2 Feature Flag State
  // ============================================
  const [useV2, setUseV2] = useState(false);

  // ============================================
  // Filter Toggle State
  // ============================================
  const [showPendingFilters, setShowPendingFilters] = useState(false); // ซ่อนไว้เหมือนประวัติ
  const [showHistoryFilters, setShowHistoryFilters] = useState(false); // ซ่อนไว้ก่อน

  // ============================================
  // Computed Values
  // ============================================
  const historyTotalPages = Math.ceil(historyTotalCount / historyLimit);

  // ✅ V2: รวม Pending และ Processing ไว้ด้วยกัน (ไม่แยก tab)
  // แสดงทั้งหมดในตารางเดียว เพื่อเห็นการเปลี่ยนสถานะจากเหลือง→น้ำเงิน
  const activeTransactions = pendingTransactions; // ใช้ทั้งหมด

  // นับจำนวนแยกตามสถานะ
  const pendingCount = pendingTransactions.filter(
    (t) => t.status === "Pending"
  ).length;
  const processingCount = pendingTransactions.filter(
    (t) => t.status === "Processing"
  ).length;

  // Stats - รวมทั้ง Pending และ Processing
  const allDeposits = pendingTransactions.filter((t) => t.type === "Deposit");
  const allWithdraws = pendingTransactions.filter((t) => t.type === "Withdraw");
  const totalDeposit = allDeposits.reduce((sum, t) => sum + t.amount, 0);
  const totalWithdraw = allWithdraws.reduce((sum, t) => sum + t.amount, 0);

  // ============================================
  // Load Pending Transactions (เหมือน pending-page)
  // ============================================
  const loadPendingData = useCallback(async () => {
    setIsPendingLoading(true);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        console.error("No session token");
        setIsPendingLoading(false);
        return;
      }

      console.log("📡 [SmartCashier] Loading pending transactions...");
      // ✅ V2: ดึงทั้ง Pending และ Processing
      const result = await TransactionService.findAll(token, {
        status: ["Pending", "Processing"] as any,
        type: pendingFilters.type as "Deposit" | "Withdraw" | undefined,
        limit: 100,
      });

      console.log(
        "✅ [SmartCashier] Loaded:",
        result.total,
        "pending/processing transactions"
      );

      // Map API response to Transaction type
      let items: Transaction[] = result.items.map((tx) => ({
        id: tx.id,
        type: tx.type,
        createFrom: (tx.createFrom || "Member") as
          | "Panel"
          | "Member"
          | "Transfer",
        amount: tx.amount,
        status: tx.status,
        memberId: tx.memberId,
        memberUsername: tx.memberUsername,
        memberName: tx.memberName,
        memberBankName: tx.memberBankName,
        memberBankAccount: tx.memberBankAccount,
        memberAgentStatus: tx.memberAgentStatus,
        isBonus: tx.isBonus,
        webBankName: tx.webBankName,
        webBankAccount: tx.webBankAccount,
        webBankId: tx.webBankId,
        withdrawBy: tx.withdrawBy as "Auto" | "Manual" | "Manaul" | undefined,
        isCreditWithdraw: tx.isCreditWithdraw,
        isCashWithdraw: tx.isCashWithdraw,
        isCreditWithdrawAmount: tx.isCreditWithdrawAmount,
        isCashWithdrawAmount: tx.isCashWithdrawAmount,
        transferId: tx.transferId, // ✅ เพิ่ม transfer_id
        uuid: tx.uuid,
        apiBankObj: tx.apiBankObj,
        notes: tx.notes,
        statusNote: tx.statusNote,
        promotionCode: tx.promotionCode,
        promotionInfo: tx.promotionInfo, // ✅ เพิ่ม promotion_info
        dateTime: tx.dateTime,
        transferToBankName: tx.transferToBankName,
        transferToBankAccount: tx.transferToBankAccount,
        transferToAccountName: tx.transferToAccountName,
        transferFromBankName: tx.transferFromBankName,
        transferFromBankAccount: tx.transferFromBankAccount,
        transferFromAccountName: tx.transferFromAccountName,
        createdAt: tx.createdAt,
        updatedAt: tx.updatedAt,
        updatedBy: tx.updatedBy,
        createdBy: tx.createdBy,
        channel: tx.channel || "",
      }));

      // Apply client-side filters
      if (pendingFilters.searchText) {
        const search = pendingFilters.searchText.toLowerCase();
        items = items.filter(
          (tx) =>
            tx.memberUsername?.toLowerCase().includes(search) ||
            tx.memberName?.toLowerCase().includes(search) ||
            tx.memberBankAccount?.includes(search) ||
            tx.transferId?.toString().includes(search) || // ✅ ค้นหาด้วย Transfer ID
            tx.uuid?.toLowerCase().includes(search) // ค้นหาด้วย UUID
        );
      }
      if (pendingFilters.channel) {
        items = items.filter((tx) => tx.createFrom === pendingFilters.channel);
      }
      if (pendingFilters.bankType === "bonus") {
        items = items.filter((tx) => tx.isBonus);
      } else if (pendingFilters.bankType === "bank") {
        items = items.filter((tx) => !tx.isBonus);
      }

      // Sort by oldest first (รายการเก่าอยู่บน)
      items.sort((a, b) => {
        const dateA = new Date(
          a.dateTime || a.datetimeCreated || a.createdAt
        ).getTime();
        const dateB = new Date(
          b.dateTime || b.datetimeCreated || b.createdAt
        ).getTime();
        return dateA - dateB; // oldest first
      });

      setPendingTransactions(items);

      // Load bank accounts
      try {
        const bankResult = await BankService.getWebBankAccounts(token);
        console.log("🏦 [SmartCashier] All banks from service:", bankResult);

        // ✅ Filter: เฉพาะบัญชีหลัก (primaryDeposit หรือ primaryWithdraw)
        const primaryBanks = bankResult.filter(
          (b) => b.primaryDeposit || b.primaryWithdraw
        );
        console.log("🏦 [SmartCashier] Primary banks after filter:", primaryBanks);

        const mappedBanks: BankAccount[] = primaryBanks.map((b) => {
          const bankInfo = findBank(b.bankName);
          return {
            id: b.id,
            name: b.name,
            bankName: bankInfo?.name || b.bankName,
            bankId: b.id,
            bankAccount: b.bankAccount,
            bankImage: bankInfo?.image || b.imageUrl,
            type: b.isBoth
              ? "Both"
              : b.type === "deposit"
              ? "Deposit"
              : "Withdraw",
            amount: b.amount,
            apiStatus: b.status === "Active" ? "active" : "suspend",
            bankApi: true,
          };
        });
        setPendingBanks(mappedBanks);
      } catch (bankError) {
        console.error("Error loading banks:", bankError);
      }
    } catch (error) {
      console.error("❌ [SmartCashier] Error loading pending:", error);
      toast.error("ไม่สามารถโหลดข้อมูลรอดำเนินการได้");
      setPendingTransactions([]);
    } finally {
      setIsPendingLoading(false);
    }
  }, [sessionToken, pendingFilters]);

  // ============================================
  // Load Bank Feed (SlipLog + LineConnectLog)
  // ============================================
  const loadBankFeed = useCallback(async () => {
    if (!sessionToken) return;

    setIsLoadingBankFeed(true);
    try {
      // ✅ ใช้ date range จาก state (default: 7 วันล่าสุด)
      // ถ้าไม่มี date range ให้ใช้ default 7 วัน
      const defaultRange = getDefaultDateRange();
      const dateRange = bankFeedDateRange || defaultRange;
      const today = dateRange.to || new Date();
      today.setHours(23, 59, 59, 999); // สิ้นสุดวันนี้
      const sevenDaysAgo = new Date(dateRange.from);
      sevenDaysAgo.setHours(0, 0, 0, 0); // เริ่มต้นวันนั้น

      // ✅ ใช้ local date เพื่อหลีกเลี่ยงปัญหา timezone
      const startDate = sevenDaysAgo.toISOString();
      const endDate = today.toISOString();

      console.log("📅 [BankFeed] Date range:", {
        startDate,
        endDate,
        startDateFormatted: sevenDaysAgo.toLocaleDateString("th-TH"),
        endDateFormatted: today.toLocaleDateString("th-TH"),
        hasDateRange: !!bankFeedDateRange,
      });

      // ✅ ดึง SlipLog (limit 1000) - ดึงทั้งหมดไม่กรอง status
      // ✅ SlipLog เป็นรายการที่ต้องมาก่อนการนำไปติม ต้องแสดงทั้งหมด
      // ✅ ส่ง date filter ไปที่ backend เพื่อประสิทธิภาพที่ดีกว่า
      const slipLogResult = await SlipLogService.getAll(sessionToken, {
        page: 1,
        limit: 1000,
        // ✅ ไม่ส่ง status เพื่อดึงทั้งหมด (รวม Pending, Approved, Rejected)
        // ✅ ไม่ส่ง verificationStatus เพื่อดึงทั้งหมด
        startDate, // ✅ ส่ง date filter ไปที่ backend (ใช้ datetime field)
        endDate, // ✅ ส่ง date filter ไปที่ backend
      });

      console.log("📊 [BankFeed] SlipLog results:", {
        total: slipLogResult.count,
        items: slipLogResult.results.length,
        sample: slipLogResult.results[0],
      });

      // ✅ ไม่ต้อง filter อีกครั้ง เพราะ filter ที่ backend แล้ว
      const filteredSlipLogs = slipLogResult.results;

      console.log("📊 [BankFeed] Filtered SlipLog:", {
        before: slipLogResult.results.length,
        after: filteredSlipLogs.length,
        dateRange: {
          from: sevenDaysAgo.toLocaleString("th-TH"),
          to: today.toLocaleString("th-TH"),
        },
      });

      // ✅ ดึง LineConnectLog (limit 1000) - ดึงทั้งหมดไม่กรอง status
      // ✅ LineConnectLog เป็นรายการที่ต้องมาก่อนการนำไปติม ต้องแสดงทั้งหมด
      // ✅ ส่ง date filter เสมอ (ใช้ dateRange ที่คำนวณแล้ว)
      const lineConnectLogResult = await LineConnectLogService.getAll(
        sessionToken,
        {
          page: 1,
          limit: 1000,
          status: "all", // ✅ ดึงทั้งหมด (รวม Pending, Approved, Rejected)
          type: "Deposit", // เฉพาะ Deposit
          used: "all", // ✅ ดึงทั้งหมด (รวม used และ unused)
          startDate, // ✅ ส่ง date filter เสมอ (ใช้ dateRange ที่คำนวณแล้ว)
          endDate, // ✅ ส่ง date filter เสมอ
        }
      );

      console.log("📊 [BankFeed] LineConnectLog results:", {
        total: lineConnectLogResult.total,
        items: lineConnectLogResult.results.length,
        sample: lineConnectLogResult.results[0],
      });

      // ✅ Bank Feed ดึงเฉพาะจาก SlipLog และ LineConnectLog เท่านั้น
      // ✅ ไม่ดึงจาก DepositWithdraw เพราะ Bank Feed เป็นรายการที่ต้องมาก่อนการนำไปติม

      // ✅ แปลง SlipLog เป็น BankFeedItem - แสดงทั้งหมด ไม่กรอง (กรองแล้วข้างบน)
      const slipFeedItems: BankFeedItem[] = filteredSlipLogs.map(
        (slip: any) => {
          // ✅ ใช้ datetime ก่อน, ไม่งั้นใช้ createdAt (เรียงเวลาล่าสุดก่อน)
          const timestamp = slip.datetime
            ? new Date(slip.datetime).toISOString()
            : slip.createdAt || new Date().toISOString();

          // ✅ ใช้ transfer_id (counter) ถ้ามี, ไม่งั้นใช้ id สั้นๆ (8 ตัวท้าย)
          const transRef = slip.transfer_id
            ? slip.transfer_id.toString()
            : slip.id.slice(-8);

          return {
            id: `slip-${slip.id}`,
            source: "SlipLog",
            transRef,
            amount: slip.amount || 0,
            senderName: slip.senderName || slip.sender || "ไม่ระบุ",
            senderBank: slip.senderBankId
              ? findBank(slip.senderBankId)?.name || slip.senderBankId
              : "ไม่ระบุ",
            senderAccount:
              slip.senderBankAccount ||
              slip.sender ||
              slip.fromAccount ||
              "ไม่ระบุ",
            receiverBank: slip.receiverBankId
              ? findBank(slip.receiverBankId)?.name || slip.receiverBankId
              : "ไม่ระบุ",
            receiverAccount:
              slip.receiverBankAccount ||
              slip.receiver ||
              slip.toAccount ||
              "ไม่ระบุ",
            timestamp,
            // ✅ isMatched = true เมื่อมี transfer_id (มี DepositWithdraw แล้ว)
            // ✅ isMatched = false เมื่อไม่มี transfer_id (ยัง unmatched - รวม pending ที่ "ไม่สามารถระบุได้")
            isMatched: !!slip.transfer_id,
            matchedMember:
              slip.memberUsername ||
              slip.memberName ||
              (slip.member_id && !slip.memberUsername && !slip.memberName
                ? "ไม่สามารถระบุได้"
                : undefined),
            status: slip.status,
            rawData: slip,
          };
        }
      );

      // ✅ แปลง LineConnectLog เป็น BankFeedItem - แสดงทั้งหมด ไม่กรอง
      const lineFeedItems: BankFeedItem[] = lineConnectLogResult.results.map(
        (line: LineConnectLog) => {
          // ✅ ใช้ dateTimeFormat (date_time_format) ก่อน เพราะเป็นวันที่เวลาของธุรกรรมจริง (ISO format)
          // ✅ dateTimeFormat เป็น ISO string (เช่น "2026-01-05T03:51:00")
          let timestamp: string;
          try {
            if (line.dateTimeFormat) {
              // ✅ dateTimeFormat เป็น ISO string แล้ว (เช่น "2026-01-05T03:51:00")
              const date = new Date(line.dateTimeFormat);
              if (!isNaN(date.getTime())) {
                timestamp = date.toISOString();
              } else {
                // ถ้า dateTimeFormat ไม่ valid ลองใช้ dateTime
                if (line.dateTime) {
                  const date2 = new Date(line.dateTime);
                  timestamp = !isNaN(date2.getTime())
                    ? date2.toISOString()
                    : line.createdAt || new Date().toISOString();
                } else {
                  timestamp = line.createdAt || new Date().toISOString();
                }
              }
            } else if (line.dateTime) {
              const date = new Date(line.dateTime);
              timestamp = !isNaN(date.getTime())
                ? date.toISOString()
                : line.createdAt || new Date().toISOString();
            } else if (line.date && line.time) {
              const combinedDateTime = `${line.date} ${line.time}`;
              const date = new Date(combinedDateTime);
              timestamp = !isNaN(date.getTime())
                ? date.toISOString()
                : line.createdAt || new Date().toISOString();
            } else {
              timestamp = line.createdAt || new Date().toISOString();
            }
          } catch (error) {
            console.warn(
              "⚠️ [SmartCashier] Error parsing LineConnectLog date:",
              line.dateTimeFormat || line.dateTime,
              error
            );
            timestamp = line.createdAt || new Date().toISOString();
          }

          // ✅ ใช้ lineId ถ้ามี, ไม่งั้นใช้ id สั้นๆ (8 ตัวท้าย)
          const transRef = line.lineId
            ? line.lineId.toString()
            : line.id.slice(-8);

          return {
            id: `line-${line.id}`,
            source: "LineConnectLog",
            transRef,
            amount: line.amount || 0,
            senderName: line.title || "ไม่ระบุ",
            senderBank: line.bank || "ไม่ระบุ",
            senderAccount: line.bankNumber || "ไม่ระบุ",
            receiverBank: line.bank || "ไม่ระบุ",
            receiverAccount: line.myNumber || "ไม่ระบุ",
            timestamp,
            isMatched: !!(line.used && line.status === "Approved"), // ✅ used + Approved = matched (มี DepositWithdraw แล้ว)
            status: line.status,
            rawData: line,
          };
        }
      );

      // ✅ Bank Feed ดึงเฉพาะจาก SlipLog และ LineConnectLog เท่านั้น
      // ✅ ไม่รวม DepositWithdraw เพราะ Bank Feed เป็นรายการที่ต้องมาก่อนการนำไปติม

      // ✅ รวมและเรียงตาม timestamp (ล่าสุดก่อน) - รวมเฉพาะ SlipLog และ LineConnectLog
      let allFeedItems = [...slipFeedItems, ...lineFeedItems].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      console.log("📊 [BankFeed] Before duplicate check:", {
        dateRange: {
          from: sevenDaysAgo.toLocaleDateString("th-TH"),
          to: today.toLocaleDateString("th-TH"),
        },
        total: allFeedItems.length,
        matched: allFeedItems.filter((i) => i.isMatched).length,
        unmatched: allFeedItems.filter((i) => !i.isMatched).length,
        slipLog: {
          total: slipLogResult.results.length,
          filtered: filteredSlipLogs.length,
        },
        lineConnectLog: {
          total: lineFeedItems.length,
          matched: lineFeedItems.filter((i) => i.isMatched).length,
        },
        // ✅ Debug: หารายการ 125 บาท
        amount125Items: allFeedItems
          .filter((i) => i.amount === 125)
          .map((i) => ({
            id: i.id,
            source: i.source,
            amount: i.amount,
            timestamp: i.timestamp,
            transRef: i.transRef,
            isMatched: i.isMatched,
          })),
      });

      // ✅ ตรวจสอบ duplicate transactions
      // ถ้ารายการจาก SlipLog และ LineConnectLog เป็นรายการเดียวกัน (amount, account, datetime ใกล้เคียง)
      // ให้ mark รายการที่มาทีหลังเป็น duplicate
      const processedItems: BankFeedItem[] = [];
      const seenTransactions = new Map<string, BankFeedItem>();

      for (const item of allFeedItems) {
        // สร้าง key สำหรับตรวจสอบ duplicate: amount + senderAccount + receiverAccount + date (ไม่รวมเวลา)
        const itemDate = new Date(item.timestamp);
        const dateKey = itemDate.toISOString().split("T")[0]; // YYYY-MM-DD
        const duplicateKey = `${item.amount}-${item.senderAccount}-${item.receiverAccount}-${dateKey}`;

        const existingItem = seenTransactions.get(duplicateKey);

        if (existingItem && !existingItem.isMatched && !item.isMatched) {
          // ถ้ามีรายการที่เหมือนกันและทั้งคู่ยัง unmatched
          // รายการที่มาทีหลัง (timestamp ใหม่กว่า) จะถูก mark เป็น duplicate
          const existingTime = new Date(existingItem.timestamp).getTime();
          const currentTime = new Date(item.timestamp).getTime();

          if (currentTime > existingTime) {
            // รายการปัจจุบันมาทีหลัง → mark เป็น duplicate
            item.isDuplicate = true;
            item.duplicateReason = `รายการซ้ำกับ ${
              existingItem.source === "SlipLog" ? "Slip" : "LINE"
            } ${existingItem.transRef}`;
            console.log(
              `⚠️ [BankFeed] Duplicate detected: ${item.id} duplicates ${existingItem.id}`
            );
          } else {
            // รายการเดิมมาทีหลัง → mark รายการเดิมเป็น duplicate
            const existingIndex = processedItems.findIndex(
              (i) => i.id === existingItem.id
            );
            if (existingIndex >= 0) {
              processedItems[existingIndex].isDuplicate = true;
              processedItems[existingIndex].duplicateReason = `รายการซ้ำกับ ${
                item.source === "SlipLog" ? "Slip" : "LINE"
              } ${item.transRef}`;
            }
            seenTransactions.set(duplicateKey, item); // อัปเดตเป็นรายการใหม่
          }
        } else if (!existingItem) {
          // รายการใหม่ → เก็บไว้
          seenTransactions.set(duplicateKey, item);
        }

        processedItems.push(item);
      }

      setBankFeed(processedItems);
      console.log("✅ [SmartCashier] Loaded bank feed (7 days):", {
        dateRange: {
          from: sevenDaysAgo.toLocaleDateString("th-TH"),
          to: today.toLocaleDateString("th-TH"),
        },
        slipLog: {
          total: slipLogResult.results.length,
          filtered: filteredSlipLogs.length,
          matched: slipFeedItems.filter((i) => i.isMatched).length,
        },
        lineConnectLog: {
          total: lineConnectLogResult.results.length,
          matched: lineFeedItems.filter((i) => i.isMatched).length,
        },
        total: processedItems.length,
        matched: processedItems.filter((i) => i.isMatched).length,
        unmatched: processedItems.filter((i) => !i.isMatched && !i.isDuplicate)
          .length,
        duplicate: processedItems.filter((i) => i.isDuplicate).length,
        // ✅ Debug: หารายการ 125 บาท
        amount125Items: processedItems
          .filter((i) => i.amount === 125)
          .map((i) => ({
            id: i.id,
            source: i.source,
            amount: i.amount,
            timestamp: i.timestamp,
            transRef: i.transRef,
            isMatched: i.isMatched,
            isDuplicate: i.isDuplicate,
          })),
      });
    } catch (error) {
      console.error("❌ [SmartCashier] Error loading bank feed:", error);
      toast.error("ไม่สามารถโหลดข้อมูล Bank Feed ได้");
      setBankFeed([]);
    } finally {
      setIsLoadingBankFeed(false);
    }
  }, [sessionToken, bankFeedDateRange]); // ✅ ลบ getDefaultDateRange ออก เพราะเป็น helper function

  // ============================================
  // Load History Transactions (เหมือน history-page)
  // ============================================
  const loadHistoryData = useCallback(async () => {
    setIsHistoryLoading(true);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        console.error("No session token");
        setIsHistoryLoading(false);
        return;
      }

      console.log("📡 [SmartCashier] Loading history...", {
        historyFilters,
        historyPage,
        historyLimit,
      });

      const result = await TransactionService.findAll(token, {
        type: historyFilters.type as "Deposit" | "Withdraw" | undefined,
        status: (historyFilters.status || undefined) as any,
        startDate: historyFilters.dateFrom || undefined,
        endDate: historyFilters.dateTo || undefined,
        page: historyPage,
        limit: historyLimit,
      });

      console.log(
        "✅ [SmartCashier] Loaded:",
        result.total,
        "history transactions"
      );

      // Map API response
      let items: Transaction[] = result.items.map((tx) => ({
        id: tx.id,
        type: tx.type,
        createFrom: (tx.createFrom || "Member") as
          | "Panel"
          | "Member"
          | "Transfer",
        amount: tx.amount,
        status: tx.status,
        memberId: tx.memberId,
        memberUsername: tx.memberUsername,
        memberName: tx.memberName,
        memberBankName: tx.memberBankName,
        memberBankAccount: tx.memberBankAccount,
        memberAgentStatus: tx.memberAgentStatus,
        isBonus: tx.isBonus,
        webBankName: tx.webBankName,
        webBankAccount: tx.webBankAccount,
        webBankId: tx.webBankId,
        withdrawBy: tx.withdrawBy as "Auto" | "Manual" | "Manaul" | undefined,
        isCreditWithdraw: tx.isCreditWithdraw,
        isCashWithdraw: tx.isCashWithdraw,
        isCreditWithdrawAmount: tx.isCreditWithdrawAmount,
        isCashWithdrawAmount: tx.isCashWithdrawAmount,
        transferId: tx.transferId, // ✅ เพิ่ม transfer_id
        uuid: tx.uuid,
        apiBankObj: tx.apiBankObj,
        notes: tx.notes,
        statusNote: tx.statusNote,
        promotionCode: tx.promotionCode,
        promotionInfo: tx.promotionInfo, // ✅ เพิ่ม promotion_info
        dateTime: tx.dateTime,
        datetimeCreated: tx.dateTime,
        transferToBankName: tx.transferToBankName,
        transferToBankAccount: tx.transferToBankAccount,
        transferToAccountName: tx.transferToAccountName,
        transferFromBankName: tx.transferFromBankName,
        transferFromBankAccount: tx.transferFromBankAccount,
        transferFromAccountName: tx.transferFromAccountName,
        createdAt: tx.createdAt,
        updatedAt: tx.updatedAt,
        updatedBy: tx.updatedBy,
        createdBy: tx.createdBy,
        channel: tx.channel || "",
      }));

      // Apply client-side filters
      if (historyFilters.searchText) {
        const search = historyFilters.searchText.toLowerCase();
        items = items.filter(
          (tx) =>
            tx.memberUsername?.toLowerCase().includes(search) ||
            tx.memberName?.toLowerCase().includes(search) ||
            tx.memberBankAccount?.includes(search) ||
            tx.transferId?.toString().includes(search) || // ✅ ค้นหาด้วย Transfer ID
            tx.uuid?.toLowerCase().includes(search) // ค้นหาด้วย UUID
        );
      }
      if (historyFilters.channel) {
        items = items.filter((tx) => tx.createFrom === historyFilters.channel);
      }
      if (historyFilters.bankType === "bonus") {
        items = items.filter((tx) => tx.isBonus);
      } else if (historyFilters.bankType === "bank") {
        items = items.filter((tx) => !tx.isBonus);
      }

      // ไม่แสดงรายการรอดำเนินการในประวัติ (ยกเว้นกรองเฉพาะ Pending หรือ Processing)
      if (
        historyFilters.status !== "Pending" &&
        historyFilters.status !== "Processing"
      ) {
        items = items.filter(
          (tx) => tx.status !== "Pending" && tx.status !== "Processing"
        );
      }

      setHistoryTransactions(items);
      setHistoryTotalCount(result.total);
    } catch (error) {
      console.error("❌ [SmartCashier] Error loading history:", error);
      toast.error("ไม่สามารถโหลดประวัติได้");
      setHistoryTransactions([]);
      setHistoryTotalCount(0);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [sessionToken, historyFilters, historyPage, historyLimit]);

  // ============================================
  // Effects
  // ============================================

  // Load V2 Feature Flag
  useEffect(() => {
    const checkV2 = async () => {
      try {
        const token =
          sessionToken ||
          sessionStorage.getItem("admin_session_token") ||
          localStorage.getItem("admin_session_token");
        if (token) {
          const v2Status = await TransactionService.checkV2Status(token);
          setUseV2(v2Status.useV2);
          console.log("📌 [SmartCashier] V2 Mode:", v2Status.useV2);
        }
      } catch (error) {
        console.error("Error checking V2 status:", error);
        setUseV2(false);
      }
    };
    checkV2();
  }, [sessionToken]);

  useEffect(() => {
    loadPendingData();
  }, [loadPendingData]);

  useEffect(() => {
    loadHistoryData();
  }, [loadHistoryData]);

  // ✅ Load Bank Feed on mount and when date range changes
  useEffect(() => {
    if (sessionToken) {
      loadBankFeed();
    }
    // ✅ เรียกเฉพาะเมื่อ sessionToken หรือ bankFeedDateRange เปลี่ยน
    // ไม่ต้องใส่ loadBankFeed ใน dependency เพราะจะทำให้ refresh บ่อยเกินไป
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, bankFeedDateRange]);

  // ✅ Auto-refresh Bank Feed every 30 seconds when sidebar is open
  useEffect(() => {
    if (!sidebarOpen || !sessionToken) return;

    const interval = setInterval(() => {
      loadBankFeed();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
    // ✅ ใช้ loadBankFeed โดยตรงใน interval ไม่ต้องใส่ใน dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, sessionToken]);

  // Auto-refresh pending every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadPendingData, 30000);
    return () => clearInterval(interval);
  }, [loadPendingData]);

  // ============================================
  // Handlers (เหมือนเดิม)
  // ============================================
  const handleRefresh = () => {
    loadPendingData();
    loadHistoryData();
  };

  const handleCreateDeposit = () => {
    setDialogMode("create-deposit");
    setSelectedTransaction(null);
    setDialogOpen(true);
  };

  const handleCreateWithdraw = () => {
    setDialogMode("create-withdraw");
    setSelectedTransaction(null);
    setDialogOpen(true);
  };

  const handleViewDetail = (tx: Transaction) => {
    const canEdit = tx.status !== "Approved" && tx.status !== "Completed";
    setDialogMode(canEdit ? "edit" : "view");
    setSelectedTransaction(tx);
    setDialogOpen(true);
  };

  const handleSaveTransaction = async (data: TransactionFormData) => {
    // ✅ ถ้าเป็นรายการที่ "ไม่สามารถระบุได้" และเลือก member แล้ว → อนุมัติต่ออัตโนมัติ
    if (selectedTransaction) {
      const isUnidentifiedMember =
        selectedTransaction.memberName?.includes("ไม่สามารถ") ||
        selectedTransaction.memberName?.includes("ไม่สามารถระบุได้") ||
        selectedTransaction.memberName?.trim() === "" ||
        !selectedTransaction.memberId;

      // ✅ ถ้าเป็นรายการที่ "ไม่สามารถระบุได้" และเลือก member ใหม่แล้ว
      if (
        isUnidentifiedMember &&
        data.memberId &&
        data.memberId !== selectedTransaction.memberId
      ) {
        // ✅ รอให้ transaction อัพเดทเสร็จก่อน (delay เล็กน้อย)
        setTimeout(async () => {
          try {
            const token =
              sessionToken ||
              sessionStorage.getItem("admin_session_token") ||
              localStorage.getItem("admin_session_token");

            if (!token) {
              toast.error(t("smartCashier.pleaseLogin"));
              return;
            }

            // ✅ อนุมัติรายการ (Deposit → Manual, Withdraw → ตาม V2)
            if (selectedTransaction.type === "Deposit") {
              await TransactionService.approveManualV2(
                token,
                selectedTransaction.id
              );
              toast.success("บันทึกและอนุมัติรายการฝากสำเร็จ");
            } else if (selectedTransaction.type === "Withdraw") {
              // ✅ Withdraw → เปิด ApproveBankDialog
              if (useV2) {
                // ดึง transaction ใหม่เพื่อดู member ที่อัพเดทแล้ว
                const updatedTx = await TransactionService.findOne(
                  token,
                  selectedTransaction.id
                );
                if (updatedTx) {
                  setApproveBankDialogTransaction(updatedTx);
                  setApproveBankDialogType("manual");
                  setApproveBankDialogOpen(true);
                }
              } else {
                await TransactionService.approveManual(
                  token,
                  selectedTransaction.id
                );
                toast.success("บันทึกและอนุมัติรายการถอนสำเร็จ");
              }
            }
          } catch (error) {
            console.error("Error approving after member selection:", error);
            toast.error("บันทึกสำเร็จ แต่เกิดข้อผิดพลาดในการอนุมัติ");
          }
        }, 500); // รอ 500ms เพื่อให้ backend อัพเดท member_obj เสร็จ
      }
    }

    toast.success("บันทึกข้อมูลสำเร็จ");
    await loadPendingData();
    await loadHistoryData();
  };

  const handleApproveManual = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      // ✅ ดึงข้อมูล transaction เพื่อเช็ค type
      const transaction =
        pendingTransactions.find((tx) => tx.id === id) ||
        historyTransactions.find((tx) => tx.id === id);

      if (!transaction) {
        toast.error("ไม่พบรายการธุรกรรม");
        return;
      }

      // ✅ เช็คว่า member เป็น "ไม่สามารถระบุได้" หรือไม่
      const isUnidentifiedMember =
        transaction.memberName?.includes("ไม่สามารถ") ||
        transaction.memberName?.includes("ไม่สามารถระบุได้") ||
        transaction.memberName?.trim() === "" ||
        !transaction.memberId;

      // ✅ ถ้าเป็น "ไม่สามารถระบุได้" → เปิด TransactionDialog ให้เลือก member ก่อน
      if (isUnidentifiedMember) {
        toast.info("กรุณาเลือกสมาชิกก่อนอนุมัติ");
        setSelectedTransaction(transaction);
        setDialogMode("edit");
        setDialogOpen(true);
        return; // หยุดที่นี่ รอให้เลือก member ก่อน
      }

      // ✅ สำหรับ Deposit และใช้ V2 → เปิด ApproveBankDialog ก่อน
      if (transaction.type === "Deposit" && useV2) {
        setApproveBankDialogTransaction(transaction);
        setApproveBankDialogType("manual");
        setApproveBankDialogOpen(true);
        return;
      }

      // ✅ ถ้าเป็น Withdraw และใช้ V2 → เปิด ApproveBankDialog
      if (useV2 && transaction.type === "Withdraw") {
        setApproveBankDialogTransaction(transaction);
        setApproveBankDialogType("manual");
        setApproveBankDialogOpen(true);
        return;
      }

      // ✅ V2: ใช้ V2 function ถ้าเปิด Feature Flag
      if (useV2) {
        await TransactionService.approveManualV2(token, id);
      } else {
        await TransactionService.approveManual(token, id);
      }
      toast.success(t("smartCashier.approveManualSuccess"));
      await loadPendingData();
      await loadHistoryData();
    } catch (error) {
      console.error("Error approving manual:", error);
      toast.error(
        `${t("smartCashier.errorOccurred")}: ${(error as Error).message}`
      );
    }
  };

  const handleApproveAuto = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      // ✅ ดึงข้อมูล transaction เพื่อเช็ค type
      const transaction =
        pendingTransactions.find((tx) => tx.id === id) ||
        historyTransactions.find((tx) => tx.id === id);

      if (!transaction) {
        toast.error("ไม่พบรายการธุรกรรม");
        return;
      }

      // ✅ เช็คว่า member เป็น "ไม่สามารถระบุได้" หรือไม่
      const isUnidentifiedMember =
        transaction.memberName?.includes("ไม่สามารถ") ||
        transaction.memberName?.includes("ไม่สามารถระบุได้") ||
        transaction.memberName?.trim() === "" ||
        !transaction.memberId;

      // ✅ ถ้าเป็น "ไม่สามารถระบุได้" → เปิด TransactionDialog ให้เลือก member ก่อน
      if (isUnidentifiedMember) {
        toast.info("กรุณาเลือกสมาชิกก่อนอนุมัติ");
        setSelectedTransaction(transaction);
        setDialogMode("edit");
        setDialogOpen(true);
        return; // หยุดที่นี่ รอให้เลือก member ก่อน
      }

      // ✅ ถ้าเป็น Withdraw และใช้ V2 → เปิด ApproveBankDialog
      if (useV2 && transaction.type === "Withdraw") {
        setApproveBankDialogTransaction(transaction);
        setApproveBankDialogType("auto");
        setApproveBankDialogOpen(true);
        return;
      }

      // ✅ V2: ใช้ V2 function ถ้าเปิด Feature Flag
      if (useV2) {
        await TransactionService.approveAutoV2(token, id);
      } else {
        await TransactionService.approveAuto(token, id);
      }
      toast.success(t("smartCashier.approveAutoSuccess"));
      await loadPendingData();
      await loadHistoryData();
    } catch (error) {
      console.error("Error approving auto:", error);
      toast.error(
        `${t("smartCashier.errorOccurred")}: ${(error as Error).message}`
      );
    }
  };

  // ✅ Handler สำหรับ ApproveBankDialog
  const handleApproveFromDialog = async (bankId: string, reason: string) => {
    if (!approveBankDialogTransaction) return;

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      const id = approveBankDialogTransaction.id;

      if (approveBankDialogType === "manual") {
        await TransactionService.approveManualV2(token, id, bankId, reason);
        toast.success(t("smartCashier.approveManualSuccess"));
      } else {
        await TransactionService.approveAutoV2(token, id, bankId, reason);
        toast.success(t("smartCashier.approveAutoSuccess"));
      }

      await loadPendingData();
      await loadHistoryData();
    } catch (error) {
      console.error("Error approving from dialog:", error);
      toast.error(
        `${t("smartCashier.errorOccurred")}: ${(error as Error).message}`
      );
      throw error; // Re-throw เพื่อให้ dialog แสดง error
    }
  };

  const handleResend = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      // ✅ V2: ใช้ V2 function ถ้าเปิด Feature Flag
      if (useV2) {
        await TransactionService.retryV2(token, id);
      } else {
        await TransactionService.resend(token, id);
      }
      toast.success("ส่งซ้ำสำเร็จ");
      await loadPendingData();
    } catch (error) {
      console.error("Error resending:", error);
      toast.error(
        `${t("smartCashier.errorOccurred")}: ${(error as Error).message}`
      );
    }
  };

  // ✅ V2: เปิด Reason Dialog แทนการ reject/cancel ทันที
  const handleReject = async (id: string) => {
    // หา transaction จากทั้ง pending และ history
    const tx =
      pendingTransactions.find((t) => t.id === id) ||
      historyTransactions.find((t) => t.id === id);

    if (!tx) {
      toast.error(t("smartCashier.transactionNotFound"));
      return;
    }

    // Pending → Reject
    setReasonDialogType("reject");
    setReasonDialogTransaction(tx);
    setReasonDialogOpen(true);
  };

  const handleCancel = async (id: string) => {
    // หา transaction จากทั้ง pending และ history
    const tx =
      pendingTransactions.find((t) => t.id === id) ||
      historyTransactions.find((t) => t.id === id);

    if (!tx) {
      toast.error(t("smartCashier.transactionNotFound"));
      return;
    }

    // Cancel (ใช้ได้ทั้ง Pending และ Processing)
    setReasonDialogType("cancel");
    setReasonDialogTransaction(tx);
    setReasonDialogOpen(true);
  };

  // ✅ Handler สำหรับ onActionClick จาก TransactionTable
  const handleActionClick = (
    tx: Transaction,
    actionType: "cancel" | "reject" | "approve_manual" | "approve_auto"
  ) => {
    if (actionType === "cancel") {
      handleCancel(tx.id);
    } else if (actionType === "reject") {
      handleReject(tx.id);
    } else if (actionType === "approve_manual") {
      // ✅ เรียก handleApproveManual โดยตรง (จะเปิด ApproveBankDialog)
      handleApproveManual(tx.id);
    } else if (actionType === "approve_auto") {
      // ✅ เรียก handleApproveAuto โดยตรง (จะเปิด ApproveBankDialog)
      handleApproveAuto(tx.id);
    }
  };

  // ✅ V2: ฟังก์ชันที่ถูกเรียกหลังจาก Dialog ยืนยัน
  const handleRejectWithReason = async (reason: string) => {
    const tx = reasonDialogTransaction;
    if (!tx) return;

    const token =
      sessionToken ||
      sessionStorage.getItem("admin_session_token") ||
      localStorage.getItem("admin_session_token");
    if (!token) {
      throw new Error(t("smartCashier.pleaseLogin"));
    }

    // ✅ V2: ใช้ V2 functions พร้อมเหตุผล
    if (useV2) {
      if (reasonDialogType === "cancel") {
        // Cancel (Processing → คืนเครดิต)
        const result = (await TransactionService.cancelV2(
          token,
          tx.id,
          reason
        )) as { success: boolean; status: string; creditRefunded?: boolean };
        if (result.creditRefunded) {
          toast.success(
            t(
              "smartCashier.cancelSuccessWithRefund",
              "ยกเลิกรายการสำเร็จ (คืนเครดิตแล้ว)"
            )
          );
        } else {
          toast.success(t("smartCashier.cancelSuccess", "ยกเลิกรายการสำเร็จ"));
        }
      } else {
        // Reject (Pending)
        await TransactionService.rejectV2(token, tx.id, reason);
        toast.success(t("smartCashier.rejectSuccess", "ปฏิเสธรายการสำเร็จ"));
      }
    } else {
      await TransactionService.reject(token, tx.id);
      toast.success(t("smartCashier.rejectSuccess"));
    }

    await loadPendingData();
    await loadHistoryData();
  };

  const handleComplete = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      // ✅ V2: ใช้ V2 function ถ้าเปิด Feature Flag
      if (useV2) {
        await TransactionService.completeV2(token, id);
      } else {
        await TransactionService.complete(token, id);
      }
      toast.success(t("smartCashier.completeSuccess"));
      await loadPendingData();
      await loadHistoryData();
    } catch (error) {
      console.error("Error completing:", error);
      toast.error(
        `${t("smartCashier.errorOccurred")}: ${(error as Error).message}`
      );
    }
  };

  // 🚫 ปฏิเสธทั้งหมด
  const handleRejectAll = async () => {
    const token =
      sessionToken ||
      sessionStorage.getItem("admin_session_token") ||
      localStorage.getItem("admin_session_token");
    if (!token) {
      toast.error(t("smartCashier.pleaseLogin"));
      return;
    }

    if (pendingTransactions.length === 0) {
      toast.error(t("smartCashier.noPendingTransactions"));
      setRejectAllDialog(false);
      return;
    }

    setIsRejectingAll(true);
    setRejectProgress({ current: 0, total: pendingTransactions.length });

    try {
      // ✅ Use Batch Reject API (much faster! ~5-10 seconds instead of minutes)
      const transaction_ids = pendingTransactions.map(tx => tx.id);

      const result = await TransactionService.batchRejectV2(token, transaction_ids);

      setIsRejectingAll(false);
      setRejectAllDialog(false);

      if (result.results.success > 0) {
        toast.success(
          `${t("smartCashier.rejectSuccessCount")} ${result.results.success} ${t(
            "smartCashier.items"
          )}`
        );
      }
      if (result.results.failed > 0) {
        toast.error(
          `${t("smartCashier.rejectFailedCount")} ${result.results.failed} ${t(
            "smartCashier.items"
          )}`
        );
      }

      await loadPendingData();
      await loadHistoryData();

    } catch (error) {
      console.error('Batch reject error:', error);
      setIsRejectingAll(false);
      setRejectAllDialog(false);
      toast.error(
        `${t("smartCashier.rejectFailed")}: ${(error as Error).message || 'Unknown error'}`
      );
    }
  };

  // 👤 View Member Detail
  const handleViewMember = async (memberId: string) => {
    if (!memberId) {
      toast.error("ไม่พบข้อมูลสมาชิก");
      return;
    }

    setIsLoadingMember(true);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error(t("smartCashier.pleaseLogin"));
        return;
      }

      const userService = await getUserService();
      const member = await userService.getMember(token, memberId);

      if (member) {
        setSelectedMember(member);
        setMemberDialogOpen(true);
      } else {
        toast.error("ไม่พบข้อมูลสมาชิก");
      }
    } catch (error) {
      console.error("Error loading member:", error);
      toast.error(t("smartCashier.errorOccurred"));
    } finally {
      setIsLoadingMember(false);
    }
  };

  // ============================================
  // Render
  // ============================================
  return (
    <div className="h-screen overflow-hidden flex">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - Fixed ~15% */}
        <div className="flex-shrink-0 p-3 space-y-3 border-b bg-background/95 backdrop-blur">
          {/* Title Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-violet-500/20">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  {t("smartCashier.title")}
                  <Badge
                    variant="secondary"
                    className="bg-primary/10 text-primary"
                  >
                    {pendingTransactions.length} {t("smartCashier.pending")}
                  </Badge>
                </h1>
                <p className="text-xs text-muted-foreground">
                  {t("smartCashier.subtitle")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="bg-success/10 text-success border-success/30 hover:bg-success/20"
                onClick={handleCreateDeposit}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {t("smartCashier.deposit")}
              </Button>
              <Button
                variant="outline"
                className="bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/20"
                onClick={handleCreateWithdraw}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {t("smartCashier.withdraw")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isPendingLoading || isHistoryLoading}
              >
                <RefreshCw
                  className={cn(
                    "w-4 h-4 mr-1.5",
                    (isPendingLoading || isHistoryLoading) && "animate-spin"
                  )}
                />
                {t("smartCashier.refresh")}
              </Button>
              {/* Bank Feed Toggle Button - หลัง Refresh */}
              <Button
                variant={sidebarOpen ? "secondary" : "outline"}
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={cn(
                  "relative",
                  !sidebarOpen &&
                    bankFeed.filter((i) => !i.isMatched).length > 0 &&
                    "border-amber-400"
                )}
              >
                <ArrowRightLeft className="w-4 h-4 mr-1.5" />
                Bank Feed
                {!sidebarOpen &&
                  bankFeed.filter((i) => !i.isMatched && !i.isDuplicate)
                    .length > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-1.5 h-5 px-1.5 text-[10px]"
                    >
                      {
                        bankFeed.filter((i) => !i.isMatched && !i.isDuplicate)
                          .length
                      }
                    </Badge>
                  )}
              </Button>
            </div>
          </div>

          {/* Balance Bar */}
          <BalanceBar banks={pendingBanks} />
        </div>

        {/* Action Zone - Pending/Processing (~40% max) */}
        <div className="flex-shrink-0 max-h-[40vh] overflow-hidden flex flex-col bg-amber-50/50 dark:bg-amber-950/10 border-b">
          {/* Tab Header */}
          <div className="flex-shrink-0 bg-amber-50/95 dark:bg-amber-950/80 backdrop-blur px-4 py-2 border-b border-amber-200/50 dark:border-amber-800/50">
            <div className="flex items-center justify-between h-8">
              {/* Left: Title + Status Badges */}
              <div className="flex items-center gap-2 min-w-[350px]">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="font-semibold text-sm">
                  {t("smartCashier.activeTransactions")}
                </span>

                {/* Status Badges - แสดงจำนวนแต่ละสถานะ */}
                <div className="flex items-center gap-1.5 ml-2">
                  {pendingCount > 0 && (
                    <Badge
                      variant="outline"
                      className="bg-amber-500/15 text-amber-700 border-amber-500/30 text-[10px] px-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse mr-1" />
                      {t("smartCashier.pending")} {pendingCount}
                    </Badge>
                  )}
                  {processingCount > 0 && (
                    <Badge
                      variant="outline"
                      className="bg-blue-500/15 text-blue-700 border-blue-500/30 text-[10px] px-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mr-1" />
                      {t("smartCashier.processing")} {processingCount}
                    </Badge>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs hover:bg-amber-200/50 ml-2"
                  onClick={() => setShowPendingFilters(!showPendingFilters)}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
                  {t("smartCashier.filter")}
                  {showPendingFilters ? (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
              </div>
              {/* Right: Stats & Actions */}
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-600 font-medium">
                  {t("smartCashier.depositAmount")}:{" "}
                  {formatCurrency(totalDeposit)} ({allDeposits.length})
                </span>
                <span className="text-orange-600 font-medium">
                  {t("smartCashier.withdrawAmount")}:{" "}
                  {formatCurrency(totalWithdraw)} ({allWithdraws.length})
                </span>
                {pendingCount > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-7"
                    onClick={() => setRejectAllDialog(true)}
                    disabled={isPendingLoading || isRejectingAll}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    {t("smartCashier.rejectAll")}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Pending Filters - Collapsible */}
          {showPendingFilters && (
            <div className="flex-shrink-0 px-4 py-2 bg-white/80 dark:bg-background/80 border-b">
              <TransactionFiltersSection
                filters={pendingFilters}
                onChange={setPendingFilters}
                showStatusFilter={false}
                showDateFilter={false}
              />
            </div>
          )}

          {/* Active Transactions Table - รวม Pending + Processing */}
          <div className="flex-1 overflow-auto">
            <Card className="rounded-none border-0">
              <CardContent className="p-0">
                <TransactionTable
                  transactions={activeTransactions}
                  isLoading={isPendingLoading}
                  activeFilterType={pendingFilters.type} // ✅ ส่ง filter type
                  onViewDetail={handleViewDetail}
                  onViewMember={handleViewMember}
                  onApproveManual={handleApproveManual}
                  onApproveAuto={handleApproveAuto}
                  onResend={handleResend}
                  onReject={handleReject}
                  onCancel={handleCancel}
                  onComplete={handleComplete}
                  onActionClick={handleActionClick}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* History Zone - Fill remaining (~45%) */}
        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          {/* History Header - โครงสร้างเดียวกับ Pending */}
          <div className="flex-shrink-0 px-4 py-2 border-b bg-slate-50/50 dark:bg-slate-900/30">
            <div className="flex items-center justify-between h-8">
              {/* Left: Icon, Title, Badge - ความกว้างเท่ากับ Pending */}
              <div className="flex items-center gap-2 min-w-[200px]">
                <History className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                <span className="font-medium text-sm text-slate-700 dark:text-slate-300">
                  {t("smartCashier.history")}
                </span>
                <Badge
                  variant="secondary"
                  className="bg-slate-200 dark:bg-slate-700"
                >
                  {historyTotalCount.toLocaleString()}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs hover:bg-slate-200/50 ml-2"
                  onClick={() => setShowHistoryFilters(!showHistoryFilters)}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />
                  {t("smartCashier.filter")}
                  {showHistoryFilters ? (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
              </div>
              {/* Right: ว่างไว้เพื่อให้ layout ตรงกัน */}
              <div className="flex items-center gap-3 text-xs" />
            </div>
          </div>

          {/* History Filters - Collapsible (ซ่อนไว้ก่อน) */}
          {showHistoryFilters && (
            <div className="flex-shrink-0 px-4 py-2 bg-white/80 dark:bg-background/80 border-b">
              <TransactionFiltersSection
                filters={historyFilters}
                onChange={setHistoryFilters}
                showStatusFilter={true}
                showDateFilter={true}
              />
            </div>
          )}

          {/* History Table - ใช้ component เดิม */}
          <div className="flex-1 overflow-auto">
            <Card className="rounded-none border-0">
              <CardContent className="p-0">
                <TransactionTable
                  transactions={historyTransactions}
                  isLoading={isHistoryLoading}
                  activeFilterType={historyFilters.type} // ✅ ส่ง filter type
                  onViewDetail={handleViewDetail}
                  onViewMember={handleViewMember}
                  onApproveManual={handleApproveManual}
                  onApproveAuto={handleApproveAuto}
                  onResend={handleResend}
                  onReject={handleReject}
                  onCancel={handleCancel}
                  onComplete={handleComplete}
                  onActionClick={handleActionClick}
                />

                {/* Pagination */}
                {historyTransactions.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-muted-foreground">
                        {t("smartCashier.showing")}{" "}
                        {(historyPage - 1) * historyLimit + 1} -{" "}
                        {Math.min(
                          historyPage * historyLimit,
                          historyTotalCount
                        )}{" "}
                        {t("smartCashier.of")} {historyTotalCount}{" "}
                        {t("smartCashier.items")}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {t("smartCashier.showing")}
                        </span>
                        <Select
                          value={String(historyLimit)}
                          onValueChange={(v) => {
                            setHistoryLimit(Number(v));
                            setHistoryPage(1);
                          }}
                        >
                          <SelectTrigger className="w-[70px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-sm text-muted-foreground">
                          {t("smartCashier.items")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage(1)}
                        disabled={historyPage === 1}
                      >
                        {t("smartCashier.firstPage")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setHistoryPage((p) => Math.max(1, p - 1))
                        }
                        disabled={historyPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm px-2 min-w-[100px] text-center">
                        {t("smartCashier.page")} {historyPage} /{" "}
                        {historyTotalPages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setHistoryPage((p) =>
                            Math.min(historyTotalPages, p + 1)
                          )
                        }
                        disabled={historyPage >= historyTotalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage(historyTotalPages)}
                        disabled={historyPage >= historyTotalPages}
                      >
                        {t("smartCashier.lastPage")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Bank Feed Sidebar */}
      <BankFeedSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        bankFeed={bankFeed}
        isLoading={isLoadingBankFeed}
        selectedDateRange={bankFeedDateRange}
        onDateChange={setBankFeedDateRange}
      />

      {/* Transaction Dialog */}
      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        transaction={selectedTransaction}
        onSave={handleSaveTransaction}
      />

      {/* Member Detail Dialog */}
      <MemberDetailDialog
        member={selectedMember}
        open={memberDialogOpen}
        onOpenChange={setMemberDialogOpen}
        onSaved={() => {
          loadPendingData();
          loadHistoryData();
        }}
      />

      {/* ✅ V2: Reason Dialog สำหรับ Cancel/Reject */}
      {reasonDialogTransaction && (
        <ReasonDialog
          open={reasonDialogOpen}
          onOpenChange={setReasonDialogOpen}
          type={reasonDialogType}
          transaction={{
            id: reasonDialogTransaction.id,
            amount: reasonDialogTransaction.amount,
            memberName: reasonDialogTransaction.memberName,
            status: reasonDialogTransaction.status,
            isCreditWithdraw: reasonDialogTransaction.isCreditWithdraw,
          }}
          onConfirm={handleRejectWithReason}
        />
      )}

      {/* ✅ ApproveBankDialog สำหรับเลือกบัญชีถอน */}
      {approveBankDialogTransaction && (
        <ApproveBankDialog
          key={approveBankDialogTransaction.id} // Force re-render when transaction changes
          open={approveBankDialogOpen}
          onOpenChange={(open) => {
            setApproveBankDialogOpen(open);
            if (!open) {
              setApproveBankDialogTransaction(null);
            }
          }}
          type={approveBankDialogType}
          currentBankId={approveBankDialogTransaction.webBankId}
          onApprove={handleApproveFromDialog}
        />
      )}

      {/* Loading overlay for member */}
      {isLoadingMember && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50">
          <div className="flex items-center gap-2 bg-card p-4 rounded-lg shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t("smartCashier.loadingMember")}</span>
          </div>
        </div>
      )}

      {/* Reject All Confirmation Dialog */}
      <AlertDialog open={rejectAllDialog} onOpenChange={setRejectAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5" />
              {t("smartCashier.confirmRejectAllTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {t("smartCashier.confirmRejectAllMessage")}{" "}
                <strong className="text-foreground">
                  {pendingTransactions.length} {t("smartCashier.totalItems")}
                </strong>{" "}
                {t("common.confirm")}?
              </p>
              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <p className="text-destructive">
                  • {t("smartCashier.withdrawItems")}: {allWithdraws.length}{" "}
                  {t("smartCashier.items")} ({formatCurrency(totalWithdraw)})
                </p>
                <p className="text-success">
                  • {t("smartCashier.depositItems")}: {allDeposits.length}{" "}
                  {t("smartCashier.items")} ({formatCurrency(totalDeposit)})
                </p>
              </div>
              <p className="text-destructive font-medium">
                ⚠️ {t("smartCashier.confirmRejectAllDesc")}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRejectingAll}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleRejectAll();
              }}
              disabled={isRejectingAll}
            >
              {isRejectingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("smartCashier.rejecting")}... ({rejectProgress.current}/
                  {rejectProgress.total})
                </>
              ) : (
                <>{t("smartCashier.confirmRejectAll")}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
